import { Component, inject, Input, OnInit } from '@angular/core';
import { ApiClientService, AuthenticationService, CachedValuesService, ToastService } from '@services/index';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AgreementNoteReminderForm, AgreementNoteRemindersForm } from '../../shared-agreement.model';
import { AgreementNoteReminder, AuthenticatedUser, User } from 'app/types';
import { combineToUtcIso, utcIsoToLocalDate, utcIsoToLocalTime } from '@helpers/datetime-helper';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { map, Observable } from 'rxjs';
import { Permission } from 'app/enums';
import { AgreementNoteReminderRequest, AgreementNoteRemindersRequest } from '@services/api-client/requests';
import { TranslateService } from '@ngx-translate/core';

@UntilDestroy()
@Component({
    selector: 'app-agreement-notes-reminders',
    templateUrl: './notes-reminders.component.html',
    styleUrl: './notes-reminders.component.scss'
})
export class AgreementNotesRemindersComponent implements OnInit {
    @Input() public noteRemindersForm: FormGroup<AgreementNoteRemindersForm>;
    @Input() public agreementId?: string;
    @Input() public autoSaveNotes = false;
    private readonly cachedValuesService = inject(CachedValuesService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly apiClientService = inject(ApiClientService);
    private readonly toastService = inject(ToastService);
    private readonly translateService = inject(TranslateService);

    public noteReminderForm: FormGroup<AgreementNoteReminderForm>;
    public showPotentialValidationMessages = false;
    public showReminder = false;
    public permissions = Permission;
    private user: AuthenticatedUser;

    public readonly users$: Observable<MultiSelectItem[]> = this.cachedValuesService.users$.pipe(
        map((data) =>
            data.map((item: User) => {
                return {
                    id: item.id,
                    name: item.firstName + ' ' + item.lastName // + ' - ' + item.email
                } as MultiSelectItem;
            })
        )
    );

    public get f() {
        return this.noteReminderForm.controls;
    }

    public get disabled() {
        return this.noteRemindersForm.disabled;
    }

    public get reminders() {
        return this.noteRemindersForm.controls.notes.value.sort((a, b) => (a.created < b.created ? 1 : -1));
    }

    public ngOnInit() {
        this.initializeForm();
        this.authenticationService.user$.pipe(untilDestroyed(this)).subscribe((u) => (this.user = u));
    }

    private initializeForm() {
        this.noteReminderForm = new FormGroup<AgreementNoteReminderForm>({
            note: new FormControl('', Validators.required),
            reminder: new FormControl(false),
            reminderDate: new FormControl<NgbDate>(null, Validators.required),
            reminderTime: new FormControl('12:00'),
            subject: new FormControl('', Validators.required),
            to: new FormControl<string[]>([], Validators.required)
        });
        this.showReminder = false;
    }

    public isDisabled() {
        return this.showReminder ? !this.noteReminderForm.valid : !this.noteReminderForm.controls.note.valid;
    }

    public addNoteReminder() {
        const reminderDateTime = combineToUtcIso(this.f.reminderDate?.value, this.f.reminderTime?.value);
        const item = {
            note: this.f.note.value,
            reminder: this.f.reminder.value,
            reminderDate: reminderDateTime,
            subject: this.f.subject?.value,
            to: this.f.to?.value,
            created: new Date().toISOString(),
            createdBy: this.user.fullname
        } as AgreementNoteReminder;
        const notes = [...(this.reminders ?? [])];
        notes.unshift(item);
        this.noteRemindersForm.controls.notes.setValue(notes);
        this.initializeForm();
        this.noteRemindersForm.markAsDirty();
        this.saveNotesIfNeeded();
    }

    public deleteNote(item: AgreementNoteReminder) {
        const index = this.reminders.indexOf(item);
        const notes = [...(this.reminders ?? [])];
        notes.splice(index, 1);
        this.noteRemindersForm.controls.notes.setValue(notes);
        this.noteRemindersForm.markAsDirty();
        this.saveNotesIfNeeded();
    }

    public utcIsoToLocalTime(date: string) {
        if (date) return utcIsoToLocalTime(date);
        return '';
    }

    public utcIsoToLocalDate(date: string) {
        if (date) return utcIsoToLocalDate(date);
        return '';
    }

    public toggleReminder() {
        this.showReminder = !this.showReminder;
    }

    private saveNotesIfNeeded() {
        if (!this.autoSaveNotes || !this.agreementId) return;

        const request = this.buildNoteRemindersRequest();
        this.apiClientService.putAgreementNoteReminders$(this.agreementId, request).subscribe({
            next: () => {
                const title = this.translateService.instant('TOAST_TITLES.SUCCESS') as string;
                const message = this.translateService.instant(
                    'FEATURES.AGREEMENTS.TOAST_UPDATE_SUCCESS.MESSAGE'
                ) as string;
                this.toastService.open(title, message, { type: 'success', progressBar: true });
                this.noteRemindersForm.markAsPristine();
            },
            error: () => {
                const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                const message = this.translateService.instant(
                    'FEATURES.AGREEMENTS.TOAST_UPDATE_ERROR.MESSAGE'
                ) as string;
                this.toastService.open(title, message, { type: 'error', progressBar: true });
            }
        });
    }

    private buildNoteRemindersRequest(): AgreementNoteRemindersRequest {
        return (this.noteRemindersForm.controls.notes.value ?? []).map((item) => {
            return {
                id: item.id,
                note: item.note,
                reminder: item.reminder,
                subject: item.subject,
                to: item.id ? [] : item.to,
                reminderDate: item.reminderDate
                //reminderDate: dateTimeFromISO(item.reminderDate)
                //reminderTime: item.reminderTime
            } as AgreementNoteReminderRequest;
        }) as AgreementNoteRemindersRequest;
    }
}
